import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Clock, BookOpen, Users, ArrowRight, ChevronRight, Award } from 'lucide-react';

const MASTERCLASSES = [
  {
    id: 'building-billion-brand',
    title: 'Building a Billion-Dollar Brand from Zero',
    instructor: 'Featured Leaders Panel',
    duration: '6 Chapters · 2h 15m',
    level: 'ADVANCED',
    category: 'Entrepreneurship',
    description: 'How women founders built household names from nothing — the real strategies, failures, and pivots behind billion-dollar brands. From first product to IPO.',
    chapters: ['The First $1,000', 'Finding Product-Market Fit', 'Scaling Without VC Money', 'Building a Team That Believes', 'The IPO Decision', 'Legacy: Building Beyond Yourself'],
    learners: 2847,
    rating: 4.9,
    img: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=600&q=80',
    color: '#C25E3E',
  },
  {
    id: 'leading-male-dominated',
    title: 'Leading in a Male-Dominated Industry',
    instructor: 'Women in Banking & Tech',
    duration: '5 Chapters · 1h 45m',
    level: 'ALL LEVELS',
    category: 'Leadership',
    description: 'Practical strategies from women who lead banks, tech companies, and governments. How to command a room, negotiate power, and build authority — without changing who you are.',
    chapters: ['The Credibility Tax', 'Negotiating From Strength', 'Building Your Inner Circle', 'When They Underestimate You', 'Mentoring the Next Generation'],
    learners: 3521,
    rating: 4.8,
    img: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&q=80',
    color: '#4A6FA5',
  },
  {
    id: 'fundraising-woman-founder',
    title: 'Fundraising as a Woman Founder',
    instructor: 'VC & Founder Insights',
    duration: '7 Chapters · 2h 30m',
    level: 'INTERMEDIATE',
    category: 'Finance',
    description: 'Only 2% of VC goes to women. This masterclass teaches you how to beat those odds — from pitch deck to term sheet, from angels to Series A and beyond.',
    chapters: ['The Funding Landscape', 'Building Your Pitch Deck', 'Finding the Right Investors', 'Negotiating Term Sheets', 'Alternative Funding', 'Bootstrapping vs Raising', 'Post-Funding: What Nobody Tells You'],
    learners: 4102,
    rating: 4.9,
    img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&q=80',
    color: '#2E8B57',
  },
  {
    id: 'digital-transformation',
    title: 'Digital Transformation for Women Leaders',
    instructor: 'Tech Leadership Series',
    duration: '6 Chapters · 2h',
    level: 'INTERMEDIATE',
    category: 'Technology',
    description: 'AI, blockchain, cloud — the tech revolution is being led by women. Learn how to drive digital change in your organization from leaders who have done it.',
    chapters: ['The Digital Mindset', 'AI for Business Leaders', 'Building Digital Teams', 'Data-Driven Decisions', 'Cybersecurity Essentials', 'Future-Proofing Your Career'],
    learners: 1893,
    rating: 4.7,
    img: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80',
    color: '#7A5BA5',
  },
  {
    id: 'social-impact-scale',
    title: 'Building Social Impact at Scale',
    instructor: 'Nonprofit & Foundation Leaders',
    duration: '5 Chapters · 1h 30m',
    level: 'ALL LEVELS',
    category: 'Social Impact',
    description: 'How to build organizations that change millions of lives. From local NGO to global foundation — the frameworks, funding, and partnerships that scale impact.',
    chapters: ['Defining Your Mission', 'Impact Measurement', 'Funding Your Vision', 'Partnerships That Scale', 'Policy & Advocacy'],
    learners: 2156,
    rating: 4.8,
    img: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&q=80',
    color: '#C4792E',
  },
  {
    id: 'cross-cultural-leadership',
    title: 'Cross-Cultural Leadership in APAC & MENA',
    instructor: 'Regional Leaders Panel',
    duration: '4 Chapters · 1h 20m',
    level: 'ADVANCED',
    category: 'Leadership',
    description: 'Leading across Dubai, Singapore, Tokyo, Mumbai, and Riyadh requires different skills. Master the art of cross-cultural communication, negotiation, and team building.',
    chapters: ['Cultural Intelligence', 'Communication Across Borders', 'Negotiation Styles by Region', 'Building Global Teams'],
    learners: 1654,
    rating: 4.9,
    img: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&q=80',
    color: '#1A8A7D',
  },
];

const MasterclassCard = ({ mc }) => (
  <div className="group bg-white border border-[#1A1A1A]/8 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col" data-testid={`masterclass-${mc.id}`}>
    <div className="relative h-48 overflow-hidden">
      <img src={mc.img} alt={mc.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" style={{ filter: 'brightness(0.8)' }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 to-transparent" />
      <div className="absolute top-4 left-4"><span className="text-[9px] tracking-[0.15em] uppercase px-2.5 py-1 text-white font-semibold" style={{ fontFamily: "'Outfit', sans-serif", background: mc.color }}>{mc.level}</span></div>
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
        <span className="text-[11px] text-white/80 flex items-center gap-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}><Clock size={12} /> {mc.duration}</span>
        <span className="text-[11px] text-white/80 flex items-center gap-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}><Users size={12} /> {mc.learners.toLocaleString()}</span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm flex items-center justify-center"><Play size={24} fill="white" className="text-white ml-1" /></div>
      </div>
    </div>
    <div className="p-6 flex-1 flex flex-col">
      <span className="text-[10px] tracking-[0.15em] uppercase mb-2 block" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: mc.color }}>{mc.category}</span>
      <h3 className="text-[18px] leading-tight mb-2 group-hover:text-[#C25E3E] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, color: '#1A1A1A' }}>{mc.title}</h3>
      <p className="text-[12px] text-[#888] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{mc.instructor}</p>
      <p className="text-[13px] leading-relaxed mb-4 flex-1" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>{mc.description}</p>
      <div className="flex items-center justify-between pt-4 border-t border-[#1A1A1A]/5">
        <div className="flex items-center gap-1"><Award size={14} className="text-[#D4AF37]" /><span className="text-[13px] font-semibold" style={{ fontFamily: "'Outfit', sans-serif", color: '#1A1A1A' }}>{mc.rating}</span></div>
        <span className="text-[11px] tracking-[0.12em] uppercase text-[#C25E3E] flex items-center gap-1" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Start Free <ChevronRight size={14} /></span>
      </div>
    </div>
    <div className="h-[3px] bg-[#C25E3E] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
  </div>
);

const MasterclassesPage = () => {
  const [filter, setFilter] = useState('all');
  const categories = ['all', ...new Set(MASTERCLASSES.map(m => m.category))];
  const filtered = filter === 'all' ? MASTERCLASSES : MASTERCLASSES.filter(m => m.category === filter);

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-32 pb-20" data-testid="masterclasses-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-16">
        <p className="text-[12px] tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: '#C25E3E' }}>MASTERCLASSES</p>
        <h1 className="text-4xl lg:text-[64px] leading-[0.95] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Learn from women<br />who <span className="text-[#C25E3E]">built</span> it.</h1>
        <p className="text-lg max-w-xl" style={{ fontFamily: "'Lora', serif", color: '#4A4A4A' }}>In-depth masterclasses drawn from the journeys of 370+ verified women leaders. Free for all community members.</p>
      </section>
      <section className="bg-[#1A1A1A] py-6 mb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-wrap justify-center gap-12 text-center">
          {[{ n: '6', l: 'MASTERCLASSES' }, { n: '16K+', l: 'LEARNERS' }, { n: '33', l: 'CHAPTERS' }, { n: '100%', l: 'FREE' }].map(s => (
            <div key={s.l}><p className="text-3xl text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.n}</p><p className="text-[9px] tracking-[0.25em] uppercase text-white/50" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.l}</p></div>
          ))}
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map(c => (
            <button key={c} onClick={() => setFilter(c)} className={`h-10 px-5 text-[11px] tracking-[0.12em] uppercase border transition-all ${filter === c ? 'bg-[#C25E3E] border-[#C25E3E] text-white' : 'border-[#1A1A1A]/15 text-[#4A4A4A] hover:border-[#C25E3E] hover:text-[#C25E3E]'}`} style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
              {c === 'all' ? 'All' : c}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{filtered.map(mc => <MasterclassCard key={mc.id} mc={mc} />)}</div>
      </section>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-20">
        <div className="bg-[#F3EFE9] p-12 lg:p-16 text-center">
          <h2 className="text-3xl lg:text-4xl mb-4 tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, color: '#1A1A1A' }}>Want to teach a masterclass?</h2>
          <p className="text-base text-[#4A4A4A] max-w-lg mx-auto mb-8" style={{ fontFamily: "'Lora', serif" }}>Share your expertise with thousands of aspiring leaders. We are looking for women who have built something extraordinary.</p>
          <Link to="/participate" className="inline-flex items-center h-14 px-10 bg-[#C25E3E] text-white text-[12px] uppercase tracking-[0.18em] hover:bg-[#1A1A1A] transition-all" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Apply to Teach <ArrowRight size={15} className="ml-2" /></Link>
        </div>
      </section>
    </div>
  );
};

export default MasterclassesPage;
